import {
  ErrorOutline as ErrorIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  Fade,
  IconButton,
  Button as MuiButton,
  Paper,
  Badge,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import * as numbers from "common/numbers";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import StatusSelect from "components/common/StatusSelectWithChecks";
import Visible from "components/common/Visible";
import { usePricing } from "components/customPricing/autoModel1/pricingHooks";
import * as labels from "config.labels";
import * as layerSupport from "domain/layerSupport";
import { NONE_PAPER_PROVIDER } from "domain/layerSupport";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TransitionGroup } from "react-transition-group";
import * as staticDataService from "services/staticDataService";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as analyticsStore from "store/analytics";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programPricingSlice from "store/programPricing";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import * as utils from "utils";

const FADE_TIMEOUT = 300;

const PRICING_METHOD = "programAutoPricing";

const getValue = (layer, field, calculatedValues, programPricing) => {
  if (field.value)
    return field.value({ layer, programPricing, calculatedValues });
  let rootData;
  switch (field.root) {
    case "programPricing":
      rootData = programPricing?.layers?.[layer.id];
      break;
    case "layer":
      rootData = layer;
      break;
    default:
      rootData = calculatedValues?.values?.layers?.[layer.id];
      break;
  }
  return rootData?.[field.key] ?? "";
};

const buildDataFromFields = (
  fields,
  layers,
  calculatedValues,
  programPricing
) => {
  return layers.reduce((acc, layer) => {
    const dataForLayer = fields.reduce((layerAcc, field) => {
      const value = getValue(layer, field, calculatedValues, programPricing);
      return { ...layerAcc, [field.key]: value };
    }, {});
    return { ...acc, [layer.id]: dataForLayer };
  }, {});
};

const getCellStyle = (layerId, highlightedLayers, isAnyHighlighted) => {
  if (highlightedLayers.includes(layerId)) {
    return {
      borderLeft: "1px solid orange",
      borderRight: "1px solid orange",
    };
  } else if (!isAnyHighlighted) {
    return {};
  }
  return {
    opacity: 0.3,
  };
};

const useHiddenLayers = ({ layers, inPlayLayerIds }) => {
  const [hiddenLayers, setHiddenLayers] = useState(
    layers.map(({ id }) => id).filter((id) => !inPlayLayerIds.includes(id))
  );

  const clearHiddenLayers = () => {
    setHiddenLayers([]);
  };

  const toggleHiddenLayer = (layerId) => {
    const layer = layers.find((layer) => layer.id === layerId);

    if (!layer) {
      return;
    }

    if (hiddenLayers.includes(layerId)) {
      setHiddenLayers(hiddenLayers.filter((id) => id !== layerId));
      return;
    } else {
      setHiddenLayers([...hiddenLayers, layerId]);
      return;
    }
  };

  return { hiddenLayers, clearHiddenLayers, toggleHiddenLayer };
};

const useHighlightedLayers = ({ hiddenLayers }) => {
  const [highlightedLayers, setHighlightedLayers] = useState([]);

  const toggleHighlight = (layerId) => {
    setHighlightedLayers((prev) =>
      prev.includes(layerId)
        ? prev.filter((id) => id !== layerId)
        : [...prev, layerId]
    );
  };

  const isAnyHighlighted = highlightedLayers.length > 0;

  useEffect(() => {
    setHighlightedLayers((prev) =>
      prev.filter((layerId) => !hiddenLayers.includes(layerId))
    );
  }, [hiddenLayers]);

  return { highlightedLayers, toggleHighlight, isAnyHighlighted };
};

const LayerPricing = () => {
  const dispatch = useDispatch();
  const calculatedValues = useSelector(analyticsStore.select)?.[PRICING_METHOD];
  const calculatedLayers = calculatedValues?.values?.layers ?? {};
  const layers = useSelector(pricingSelectors.selectResolvedLayers);
  const programPricing = useSelector(programPricingSlice.select);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const isVisible = useSelector(userSelectors.selectIsVisible);

  const { isFetching, refetch } = usePricing({ pricingMethod: PRICING_METHOD });

  const inPlayLayerIds = layerSupport.extractInPlayLayerIds(layers);

  const {
    hiddenLayers,
    clearHiddenLayers,
    toggleHiddenLayer,
  } = useHiddenLayers({
    layers,
    inPlayLayerIds,
  });

  const {
    highlightedLayers,
    isAnyHighlighted,
    toggleHighlight,
  } = useHighlightedLayers({
    hiddenLayers,
  });

  const { data: reinsurersConfig } = staticDataService.useConfigQuery(
    "reinsurers"
  );
  const paperProviderMapping = Object.fromEntries(
    (reinsurersConfig?.reinsurers ?? []).map(({ key, name }) => [key, name])
  );

  const fields = [
    {
      key: "paperProvider",
      label: translateLabel(labels.AUTO_LAYER_PRICING_PAPER_PROVIDER_LABEL, {
        default: "Paper Provider",
      }),
      renderCell: ({ layer }) => {
        const value = layer.paperProvider;
        const display =
          value === NONE_PAPER_PROVIDER
            ? "None"
            : paperProviderMapping?.[value] ?? value;
        return <Typography>{display}</Typography>;
      },
      visible: true,
    },
    {
      key: "premium",
      label: translateLabel(labels.AUTO_LAYER_PRICING_LAYER_PREMIUM_LABEL, {
        default: "Technical",
      }),
      root: "layerPricing",
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "rol",
      label: translateLabel(labels.AUTO_LAYER_PRICING_ROL_LABEL, {
        default: "ROL",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "brokerTargetGrossPremium",
      label: translateLabel(labels.AUTO_LAYER_PRICING_BROKER_TARGET_LABEL, {
        default: "Broker Target",
      }),
      root: "layer",
      valueType: "currency",
      onChange: "updateLayer",
      readOnly: false,
      visible: true,
    },
    {
      key: "rolTarget",
      label: translateLabel(labels.AUTO_LAYER_PRICING_ROL_TARGET_LABEL, {
        default: "Broker Target ROL",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "quotedGrossPremium",
      root: "layer",
      label: translateLabel(labels.AUTO_LAYER_PRICING_OUR_QUOTE_LABEL, {
        default: "Our Quote",
      }),
      onChange: "updateLayer",
      valueType: "currency",
      readOnly: false,
      visible: true,
    },
    {
      key: "rolQuote",
      label: translateLabel(labels.AUTO_LAYER_PRICING_OUR_QUOTE_ROL_LABEL, {
        default: "Our Quote ROL",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "grossPremium",
      root: "layer",
      label: translateLabel(labels.AUTO_LAYER_PRICING_FOT_LABEL, {
        default: "FOT",
      }),
      onChange: "updateLayer",
      valueType: "currency",
      readOnly: false,
      visible: true,
    },
    {
      key: "rolFot",
      root: "layerPricing",
      label: translateLabel(labels.AUTO_LAYER_PRICING_FOT_ROL_LABEL, {
        default: "FOT ROL",
      }),
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "guPremium",
      label: translateLabel(labels.AUTO_LAYER_PRICING_GU_PREMIUM_LABEL, {
        default: "Ground-Up Technical",
      }),
      root: "layerPricing",
      valueType: "currency",
      dp: 0,
      readOnly: true,
      visible: true,
    },
    {
      key: "ilfFactor",
      label: translateLabel(labels.AUTO_LAYER_PRICING_ILF_FACTOR_LABEL, {
        default: "ILF Factor",
      }),
      root: "layerPricing",
      valueType: "float",
      dp: 3,
      readOnly: true,
      visible: true,
    },
  ].filter(
    ({ key }) => isVisible(`<AutoLayerPricing>.layerPricing.${key}`) ?? true
  );

  const fieldData = buildDataFromFields(
    fields,
    layers,
    calculatedValues,
    programPricing
  );

  const renderCell = (layer, field) => {
    if (field.renderCell) {
      return field.renderCell({ layer });
    }

    const updateProgramPricing = (layerId, key, value) => {
      const updatedLayers = programPricing?.layers ?? {};
      const currentLayer = updatedLayers[layerId] ?? {};
      dispatch(
        programPricingSlice.update({
          layers: {
            ...updatedLayers,
            [layerId]: {
              ...currentLayer,
              [key]: value,
            },
          },
        })
      );
    };
    const updateLayer = (layerId, key, value) => {
      const values = {
        [key]: value,
      };
      if (key === "lineSize") {
        values.shareOfLine = null;
      }

      dispatch(
        pricingActions.updateLayer({
          layerId: layerId,
          values,
        })
      );
    };
    const value = fieldData[layer.id]?.[field.key] ?? null;
    if (field.readOnly) {
      if (value == null || value === "") {
        return <></>;
      } else if (field.valueType === "percentage") {
        return (
          <Tooltip title={value}>
            <Typography>{numbers.percentage(field.dp ?? 3)(value)}</Typography>
          </Tooltip>
        );
      } else if (field.valueType === "currency") {
        return (
          <Tooltip title={value}>
            <Typography>{numbers.currencyFormat(value)}</Typography>
          </Tooltip>
        );
      } else {
        return (
          <Tooltip title={value}>
            <Typography>{numbers.dpString(field.dp ?? 0)(value)}</Typography>
          </Tooltip>
        );
      }
    }
    return (
      <Disabled ifReadOnly>
        <PrettyNumberTextField
          type={"text"}
          size={"small"}
          hiddenLabel
          suffix={field.valueType === "percentage" ? "%" : ""}
          prefix={field.valueType === "currency" ? "$" : ""}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            style: {
              textAlign: "right",
            },
          }}
          value={value}
          onChangeNumber={
            field.onChange === "updateLayer"
              ? (value) => updateLayer(layer.id, field.key, value)
              : (value) => updateProgramPricing(layer.id, field.key, value)
          }
          variant={"filled"}
          readOnly
        />
      </Disabled>
    );
  };

  const renderRow = (field, index) => {
    const rowContent = (
      <TableRow key={index}>
        <TableCell align={"right"} sx={{ minWidth: "200px" }}>
          <Typography fontSize={"0.7rem"}>{field.label}</Typography>
        </TableCell>
        <TransitionGroup component={null}>
          {layers
            .filter((layer) => !hiddenLayers.includes(layer.id))
            .map((layer) => (
              <Fade key={layer.id} timeout={FADE_TIMEOUT} appear={false}>
                <TableCell
                  align={"right"}
                  style={{
                    ...getCellStyle(
                      layer.id,
                      highlightedLayers,
                      isAnyHighlighted
                    ),
                  }}
                >
                  {renderCell(layer, field)}
                </TableCell>
              </Fade>
            ))}
        </TransitionGroup>
        <TableCell>{/* Extra space */}</TableCell>
      </TableRow>
    );

    if (field.visibilityTag) {
      return (
        <Visible
          byTag={field.visibilityTag}
          defaultVisible={field.defaultVisible}
          key={index}
        >
          {rowContent}
        </Visible>
      );
    }

    return rowContent;
  };

  return (
    <Component
      title={translateLabel(labels.AUTO_LAYER_PRICING_TITLE, {
        default: "Summary",
      })}
      isFetching={isFetching}
      options={
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <MuiButton
              onClick={() => clearHiddenLayers()}
              variant={"text"}
              disabled={hiddenLayers.length === 0}
              sx={{
                m: 1,
                display: "flex",
                width: "150px",
                justifyContent: "flex-end",
                padding: "0 16px",
              }}
              endIcon={
                <VisibilityIcon
                  size={"m"}
                  color={hiddenLayers.length > 0 ? "primary" : "disabled"}
                />
              }
            >
              {hiddenLayers.length > 0 && (
                <Typography fontSize={"0.8rem"} sx={{ mr: 1 }}>
                  {hiddenLayers.length} {"hidden"}
                </Typography>
              )}
            </MuiButton>
          </Stack>
          <Disabled ifReadOnly>
            <Button onClick={refetch} isLoading={isFetching}>
              {translateLabel(
                labels.AUTO_LAYER_PRICING_CALCULATE_BUTTON_MESSAGE,
                {
                  default: "Calculate",
                }
              )}
            </Button>
          </Disabled>
        </Stack>
      }
    >
      <Stack direction={"column"} spacing={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align={"right"} width={200}></TableCell>
                <TransitionGroup component={null}>
                  {layers
                    .filter((layer) => !hiddenLayers.includes(layer.id))
                    .map((layer) => {
                      const errors = calculatedLayers[layer.id]?.error;
                      const errorCount = errors?.length || 0;
                      const errorMessage = errors
                        ? errors.map((error) => error.message).join(", ")
                        : "";

                      return (
                        <Fade
                          key={layer.id}
                          timeout={FADE_TIMEOUT}
                          appear={false}
                        >
                          <TableCell
                            align={"right"}
                            width={200}
                            sx={{
                              mt: 1,
                              "&:hover .hover-icons": {
                                visibility: "visible",
                              },
                            }}
                          >
                            <Stack direction={"column"} spacing={0}>
                              <Stack
                                direction={"row"}
                                spacing={0}
                                justifyContent={"center"}
                                alignItems={"center"}
                                sx={{
                                  ml: 1,
                                  minHeight: "40px",
                                }}
                              >
                                <IconButton
                                  onClick={() => toggleHighlight(layer.id)}
                                  className={"hover-icons"}
                                  sx={{
                                    ml: 1,
                                    visibility: "hidden",
                                  }}
                                >
                                  <StarIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() => toggleHiddenLayer(layer.id)}
                                  className={"hover-icons"}
                                  sx={{
                                    ml: 1,
                                    visibility: "hidden",
                                  }}
                                >
                                  <VisibilityOffIcon />
                                </IconButton>
                              </Stack>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip title={layer.status}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <StatusBadgeV2
                                        status={layer.status}
                                        variant={"icon"}
                                      />
                                    </Box>
                                    <Typography
                                      fontSize={"0.8rem"}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {utils.shortLayerName(layer)}
                                    </Typography>
                                    <Visible
                                      byTag={
                                        "<AutoLayerPricing>.layerPricing.statusSelector"
                                      }
                                      defaultVisible={true}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <StatusSelect
                                          layer={layer}
                                          variant={"switch"}
                                          status={layer?.status ?? null}
                                          onChange={(status) => {
                                            dispatch(
                                              pricingActions.updateLayer({
                                                layerId: layer.id,
                                                values: { status },
                                              })
                                            );
                                          }}
                                        />
                                      </Box>
                                    </Visible>
                                  </Box>
                                </Tooltip>
                              </Box>
                              {/* Error Badge with Tooltip */}
                              {errorCount > 0 && (
                                <Stack
                                  direction={"row"}
                                  spacing={1}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                >
                                  <Tooltip title={errorMessage}>
                                    <Badge
                                      badgeContent={errorCount}
                                      color={"error"}
                                    >
                                      <ErrorIcon
                                        variant={"outlined"}
                                        color={"error"}
                                      />
                                    </Badge>
                                  </Tooltip>
                                </Stack>
                              )}
                            </Stack>
                          </TableCell>
                        </Fade>
                      );
                    })}
                </TransitionGroup>
                <TableCell>{/* Extra space */}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields
                .filter((field) => field.visible)
                .map((field, index) => renderRow(field, index))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Component>
  );
};

export default LayerPricing;
